import { FunctionComponent, useCallback, useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'

import config from '@config'
import usePageMetadata, { PageMetadata } from '@hooks/usePageMetadata'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import { StoryAPIProps, ToplistAPIProps } from '@widgets/Toplist'
import Story from '@components/Toplist/Story'
import Wrapper from '@components/Toplist/Wrapper'

type MostReadProps = Pick<ToplistAPIProps, 'title' | 'hideOnDesktop'>

type MostReadStoryProps = StoryAPIProps & {
  order: number
}

const {
  recommendation: { apiUrl: recommendationsApiUrl },
  publication: { publication },
} = config

const getMostRead = async (id: string) => {
  let responseData = []

  try {
    responseData = (
      await (
        await fetch(
          `${recommendationsApiUrl}/${`v2/mostread/${id}/?pub=${publication}`}`,
          {
            credentials: 'include',
          }
        )
      ).json()
    ).data
      ?.filter?.((story: any) => Object.keys(story).length > 0)
      ?.slice(0, 3)
  } catch {}

  return responseData
}

const isValidStories = (stories?: any[]): stories is StoryAPIProps[] =>
  (stories as any[])?.filter?.((story: any) => Object.keys(story).length > 0)
    ?.length > 2

const MostReadStory: FunctionComponent<MostReadStoryProps> = ({
  articleId,
  title,
  catchword,
  link,
  isPlus,
  aureusOfferId,
  order,
}) => {
  const onClick = useCallback(
    () => ({
      event: 'select_content',
      placement: 'article-recommendations-most-read',
      link_id: link?.href,
    }),
    [link?.href]
  )

  const trackOnClick = useTracking(onClick)

  return (
    <Story
      key={articleId}
      order={order}
      title={title}
      catchword={catchword}
      articleId={articleId}
      link={link}
      isPlus={isPlus}
      aureusOfferId={aureusOfferId}
      onClick={trackOnClick}
    />
  )
}

const MostRead: FunctionComponent<MostReadProps> = ({
  title,
  hideOnDesktop,
}) => {
  const pageMetadata = usePageMetadata()

  const id = (pageMetadata as PageMetadata)?.id

  const { data: apiToplistData } = useQuery<StoryAPIProps[]>({
    queryKey: ['most-read', id],
    queryFn: () => getMostRead(id as string),
    enabled: !!id,
    gcTime: 0,
  })

  const finalStories = useMemo(
    () =>
      isValidStories(apiToplistData) ? (
        <>
          {apiToplistData.map((story, index) => (
            <MostReadStory key={index} {...story} order={index + 1} />
          ))}
        </>
      ) : null,
    [apiToplistData]
  )

  const onImpressionTracking = useCallback<TrackingFnType>(
    () => ({
      event: 'content_impression',
      placement: 'article-recommendations-most-read',
      link_id: pageMetadata.url,
    }),
    [pageMetadata.url]
  )

  const onImpression = useTracking(onImpressionTracking)

  return (
    <Wrapper
      title={title}
      hideOnDesktop={hideOnDesktop}
      onImpression={onImpression}>
      {finalStories}
    </Wrapper>
  )
}

export default MostRead
